import { jwtDecode } from 'jwt-decode'


const TOKEN_KEY = 'access_token'
const ID_TOKEN_KEY = 'id_token'
const SSO_RETURN_PATH_KEY = 'spa_sso_return_path'

const LOCAL_AUTH_MODE = import.meta.env.VITE_LOCAL_AUTH_MODE || 'off'
const KEYCLOAK_REDIRECT_URI_OVERRIDE = (import.meta.env.VITE_KEYCLOAK_REDIRECT_URI || '').trim()
const KEYCLOAK_IDP_HINT = (import.meta.env.VITE_KEYCLOAK_IDP_HINT || '').trim()

const isLocalHost = () => {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost')
}

const shouldUseLocalMockAuth = () => {
  if (LOCAL_AUTH_MODE === 'off') return false
  if (LOCAL_AUTH_MODE === 'mock') return true
  return isLocalHost()
}

const toBase64Url = (value) =>
  btoa(JSON.stringify(value))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')

const createMockJwt = () => {
  const now = Math.floor(Date.now() / 1000)
  const payload = {
    exp: now + 8 * 60 * 60,
    iat: now,
    preferred_username: 'local.admin',
    given_name: 'Local',
    family_name: 'Admin',
    name: 'Local Admin',
    resource_access: {
      spa: {
        roles: ['ROL-ADMIN', 'AREA-DEVOPS'],
      },
    },
  }

  return `${toBase64Url({ alg: 'none', typ: 'JWT' })}.${toBase64Url(payload)}.`
}

const getEnvTrimmed = (key, fallback = '') => {
  const value = import.meta.env[key]
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  return fallback
}

const getKeycloakBaseUrl = () => {
  const rawServer = getEnvTrimmed('VITE_KEYCLOAK_SERVER_ADDR', '')
  const rawBasePath = getEnvTrimmed('VITE_KEYCLOAK_BASE_PATH', '/auth')
  const server = rawServer.endsWith('/') ? rawServer.slice(0, -1) : rawServer
  if (!rawBasePath) return server
  const basePath = rawBasePath.startsWith('/') ? rawBasePath : `/${rawBasePath}`
  const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
  return `${server}${normalizedBasePath}`
}

const getDefaultReturnPath = (pathname) => (pathname === '/' ? '/normas' : pathname)

const sanitizeCode = (value) => {
  if (!value) return null
  return value
    .split('&')[0]
    .split('#')[0]
    .trim()
    .replace(/ /g, '+') || null
}

const extractAuthCode = (search = '') => {
  const fromSearch = search.match(/[?&]code=([^&#]+)/)
  if (fromSearch?.[1]) {
    try {
      return sanitizeCode(decodeURIComponent(fromSearch[1]))
    } catch {
      return sanitizeCode(fromSearch[1])
    }
  }

  if (typeof window !== 'undefined') {
    const fromHref = window.location.href.match(/[?#&]code=([^&#]+)/)
    if (fromHref?.[1]) {
      try {
        return sanitizeCode(decodeURIComponent(fromHref[1]))
      } catch {
        return sanitizeCode(fromHref[1])
      }
    }
  }

  return null
}

const getHashParams = () => {
  if (typeof window === 'undefined') return new URLSearchParams('')

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash

  if (!hash) return new URLSearchParams('')

  const hashQuery = hash.includes('?') ? hash.split('?')[1] : hash
  return new URLSearchParams(hashQuery)
}

const redirectToAuthPage = (url, { replace = false } = {}) => {
  if (typeof window === 'undefined') return

  const navigate = (targetWindow) => {
    if (replace) targetWindow.location.replace(url)
    else targetWindow.location.assign(url)
  }

  try {
    if (window.self !== window.top && window.top) {
      navigate(window.top)
      return
    }
  } catch {
    // If top navigation is blocked by browser policies, fallback to current frame.
  }

  navigate(window)
}

export const authService = {
  getRedirectUri() {
    if (KEYCLOAK_REDIRECT_URI_OVERRIDE) {
      return KEYCLOAK_REDIRECT_URI_OVERRIDE
    }

    if (typeof window !== 'undefined') {
      const origin = window.location.origin
      return origin.endsWith('/') ? origin : `${origin}/`
    }

    return getEnvTrimmed('VITE_SERVER_FRONT_ADDR', '')
  },

  buildAuthorizationUrl({ prompt = 'login', maxAge } = {}) {
    const params = new URLSearchParams({
      client_id: getEnvTrimmed('VITE_KEYCLOAK_CLIENT_ID', ''),
      redirect_uri: this.getRedirectUri(),
      response_type: 'code',
      scope: 'openid',
      prompt,
    })

    // `kc_idp_hint=` (empty) asks Keycloak to show local login form instead of default broker redirect.
    if (KEYCLOAK_IDP_HINT.toLowerCase() === 'none') {
      params.set('kc_idp_hint', '')
    } else if (KEYCLOAK_IDP_HINT) {
      params.set('kc_idp_hint', KEYCLOAK_IDP_HINT)
    }

    if (maxAge !== undefined) {
      params.set('max_age', String(maxAge))
    }

    return `${getKeycloakBaseUrl()}/realms/${getEnvTrimmed('VITE_KEYCLOAK_REALM', '')}/protocol/openid-connect/auth?${params.toString()}`
  },

  setSsoReturnPath(path) {
    sessionStorage.setItem(SSO_RETURN_PATH_KEY, path)
  },

  consumeSsoReturnPath(fallbackPath = '/normas') {
    const path = sessionStorage.getItem(SSO_RETURN_PATH_KEY) || fallbackPath
    sessionStorage.removeItem(SSO_RETURN_PATH_KEY)
    return path
  },

  startInteractiveLogin(returnPath = '/normas') {
    if (shouldUseLocalMockAuth()) {
      const mockToken = createMockJwt()
      this.saveAccessJWT({
        access_token: mockToken,
        id_token: mockToken,
      })
      const targetPath = returnPath || '/normas'
      redirectToAuthPage(targetPath)
      return
    }

    this.setSsoReturnPath(returnPath)
    redirectToAuthPage(this.buildAuthorizationUrl({ prompt: 'login', maxAge: 0 }))
  },

  getSessionState() {
    const token = this.getAccessJWT()
    if (token === 'no_token') {
      return { isLoggedIn: false, userRol: 'PUBLIC', userName: '', userDisplay: '' }
    }

    if (this.checkTokenExpiry()) {
      this.removeToken()
      return { isLoggedIn: false, userRol: 'PUBLIC', userName: '', userDisplay: '', expired: true }
    }

    const rol = this.getUserRol()
    const area = this.getUserDataArea()[0]
    const name = this.getUserDisplayName()
    const safeArea = area || ''
    const safeName = name || ''
    const displayParts = [safeArea, safeName, rol.toLowerCase()].filter(Boolean)

    return {
      isLoggedIn: true,
      userRol: rol,
      userName: safeName,
      userDisplay: displayParts.join(' | '),
    }
  },

  async processOAuthCallback(search, fallbackPath) {
    const params = new URLSearchParams(search)
    const hashParams = getHashParams()
    const code = extractAuthCode(search)
    const error = params.get('error') || hashParams.get('error')
    const accessToken = params.get('access_token') || hashParams.get('access_token')
    const idToken = params.get('id_token') || hashParams.get('id_token') || ''

    if (error) {
      return {
        handled: true,
        returnPath: this.consumeSsoReturnPath(getDefaultReturnPath(fallbackPath)),
        session: this.getSessionState(),
      }
    }

    if (accessToken) {
      const saved = this.saveAccessJWT({
        access_token: accessToken,
        id_token: idToken,
      })

      if (!saved) {
        this.removeToken()
        return {
          handled: true,
          failed: true,
          returnPath: this.consumeSsoReturnPath(getDefaultReturnPath(fallbackPath)),
        }
      }

      return {
        handled: true,
        returnPath: this.consumeSsoReturnPath(getDefaultReturnPath(fallbackPath)),
        session: this.getSessionState(),
      }
    }

    if (!code) {
      return { handled: false }
    }

    const ok = await this.getTokenWithTempCode(code)
    const returnPath = this.consumeSsoReturnPath(getDefaultReturnPath(fallbackPath))

    if (!ok) {
      this.removeToken()
      return { handled: true, failed: true, returnPath }
    }

    return {
      handled: true,
      returnPath,
      session: this.getSessionState(),
    }
  },

  async initAuth({ pathname, search }) {
    const callback = await this.processOAuthCallback(search, pathname)

    if (callback.handled) {
      if (callback.failed) {
        return {
          action: 'ready',
          returnPath: callback.returnPath,
          isLoggedIn: false,
          userRol: 'PUBLIC',
          userName: '',
          userDisplay: '',
        }
      }

      return {
        action: 'ready',
        returnPath: callback.returnPath,
        ...callback.session,
      }
    }

    const session = this.getSessionState()

    return {
      action: 'ready',
      returnPath: pathname,
      ...session,
    }
  },
  getAccessJWT() {
    return localStorage.getItem(TOKEN_KEY) || 'no_token'
  },

  saveAccessJWT(tokenResponse) {
    try {
      localStorage.setItem(TOKEN_KEY, tokenResponse.access_token)
      if (tokenResponse.id_token) {
        localStorage.setItem(ID_TOKEN_KEY, tokenResponse.id_token)
      } else {
        localStorage.removeItem(ID_TOKEN_KEY)
      }
      return true
    } catch (err) {
      console.error('Error saving JWT:', err)
      return false
    }
  },

  getIdToken() {
    return localStorage.getItem(ID_TOKEN_KEY) || ''
  },

  removeToken() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ID_TOKEN_KEY)
  },

  
  getUserData() {
    const token = this.getAccessJWT()
    if (token === 'no_token') return null
    try {
      return jwtDecode(token)
    } catch {
      return null
    }
  },

  checkTokenExpiry() {
    const token = this.getAccessJWT()
    if (token === 'no_token') return true
    try {
      const decoded = jwtDecode(token)
      return decoded.exp * 1000 < Date.now()
    } catch {
      return true
    }
  },

  getUserRol() {
    const token = this.getAccessJWT()
    if (token === 'no_token') return 'PUBLIC'

    try {
      const data = this.getUserData()
      const spaRoles = data?.resource_access?.spa?.roles || []
      const rolEntry = spaRoles.find(r => r.startsWith('ROL-'))
      if (rolEntry) return rolEntry.substring(4)
      return 'PUBLIC'
    } catch {
      return 'PUBLIC'
    }
  },

  getUserDataArea() {
    if (this.getUserRol() === 'PUBLIC') return ['']
    try {
      const data = this.getUserData()
      const roles = data?.resource_access?.spa?.roles || []
      return roles
        .filter(r => r.startsWith('AREA'))
        .map(r => r.substring(5)) || ['']
    } catch {
      return ['']
    }
  },

  getUserDisplayName() {
    const data = this.getUserData()
    if (!data) return ''

    const fullName = `${data.given_name || ''} ${data.family_name || ''}`.trim()
    if (fullName) return fullName

    if (data.name && String(data.name).trim()) return String(data.name).trim()
    if (data.preferred_username && String(data.preferred_username).trim()) return String(data.preferred_username).trim()
    if (data.email && String(data.email).trim()) return String(data.email).trim()

    return ''
  },

  async getTokenWithTempCode(code) {
    const params = new URLSearchParams()
    params.set('code', code)
    params.set('client_id', getEnvTrimmed('VITE_KEYCLOAK_CLIENT_ID', ''))
    params.set('grant_type', 'authorization_code')
    params.set('redirect_uri', this.getRedirectUri())

    const url = `${getKeycloakBaseUrl()}/realms/${getEnvTrimmed('VITE_KEYCLOAK_REALM', '')}/protocol/openid-connect/token`

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    })

    if (!response.ok) return false

    const data = await response.json()
    return this.saveAccessJWT(data)
  },

  logout() {
    if (shouldUseLocalMockAuth()) {
      this.removeToken()
      window.location.assign('/normas')
      return
    }

    const idToken = this.getIdToken()
    this.removeToken()
    const logoutParams = new URLSearchParams({
      post_logout_redirect_uri: this.getRedirectUri(),
      redirect_uri: this.getRedirectUri(),
      client_id: getEnvTrimmed('VITE_KEYCLOAK_CLIENT_ID', ''),
    })

    if (idToken) {
      logoutParams.set('id_token_hint', idToken)
    }

    redirectToAuthPage(
      `${getKeycloakBaseUrl()}/realms/${getEnvTrimmed('VITE_KEYCLOAK_REALM', '')}/protocol/openid-connect/logout?${logoutParams.toString()}`,
      { replace: true }
    )
  },
}

export default authService

